import React, { useState, useEffect } from 'react';
import { Users, Phone, Clock, Smile, Frown } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import { analyticsAPI } from '../../../services/api';

const RankingContatos = ({ filtros }) => {
  const { darkMode } = useTheme();
  const [contatos, setContatos] = useState([]);
  const [loading, setLoading] = useState(true);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const clienteId = user.cliente_id;

  useEffect(() => {
    if (!clienteId) return;
    setLoading(true);
    const params = {};
    if (filtros?.dataInicio) params.dataInicio = filtros.dataInicio;
    if (filtros?.dataFim) params.dataFim = filtros.dataFim;
    if (filtros?.instanciaId) params.instanciaId = filtros.instanciaId;

    analyticsAPI.getContatosAtivos(clienteId, params)
      .then(res => setContatos(res.data || []))
      .catch(() => setContatos([]))
      .finally(() => setLoading(false));
  }, [clienteId, filtros?.dataInicio, filtros?.dataFim, filtros?.instanciaId]);

  if (loading) {
    return (
      <div className={`p-4 sm:p-6 rounded-xl animate-pulse ${darkMode ? 'bg-zinc-800' : 'bg-white'}`}>
        <div className={`h-5 ${darkMode ? 'bg-zinc-700' : 'bg-gray-200'} rounded w-1/3 mb-4 sm:mb-6`}></div>
        <div className="space-y-3">{[1,2,3,4,5].map(i => <div key={i} className={`h-14 ${darkMode ? 'bg-zinc-700' : 'bg-gray-200'} rounded`}></div>)}</div>
      </div>
    );
  }

  if (contatos.length === 0) return null;

  const max = contatos[0]?.total_mensagens || 1;

  const formatDate = (d) => new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

  return (
    <div className={`p-4 sm:p-6 rounded-xl border ${darkMode ? 'bg-zinc-800 border-gray-700' : 'bg-white border-gray-200'}`}>
      <h3 className={`text-base sm:text-lg font-semibold flex items-center gap-2 mb-4 sm:mb-6 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
        <Users className="w-4 h-4 sm:w-5 sm:h-5" /> Contatos mais ativos
        <span className={`text-xs font-normal px-2 py-0.5 rounded-full ${darkMode ? 'bg-zinc-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
          Top {contatos.length}
        </span>
      </h3>

      <div className="space-y-3">
        {contatos.map((c, i) => {
          const pct = (c.total_mensagens / max) * 100;
          return (
            <div key={i} className={`p-3 sm:p-4 rounded-lg relative overflow-hidden ${darkMode ? 'bg-zinc-700/30' : 'bg-gray-50'}`}>
              {/* Barra de fundo */}
              <div className={`absolute inset-y-0 left-0 ${darkMode ? 'bg-blue-900/20' : 'bg-blue-50'}`}
                style={{ width: `${pct}%` }} />

              <div className="relative flex items-center gap-2 sm:gap-4">
                {/* Posição */}
                <span className={`text-base sm:text-lg font-bold w-6 sm:w-7 text-center ${i < 3 ? 'text-blue-500' : (darkMode ? 'text-gray-500' : 'text-gray-400')}`}>
                  {i + 1}
                </span>

                {/* Avatar */}
                <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold flex-shrink-0 ${darkMode ? 'bg-zinc-600 text-white' : 'bg-gray-200 text-gray-700'}`}>
                  {(c.nome_contato || c.telefone || '?').charAt(0).toUpperCase()}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className={`font-semibold truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {c.nome_contato || c.telefone}
                  </p>
                  <div className={`hidden sm:flex items-center gap-3 text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{c.telefone}</span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatDate(c.ultimo_contato)}</span>
                  </div>
                </div>

                {/* Métricas */}
                <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                  {c.positivas > 0 && (
                    <span className="hidden sm:flex items-center gap-1 text-xs text-green-500">
                      <Smile className="w-3.5 h-3.5" />{c.positivas}
                    </span>
                  )}
                  {c.negativas > 0 && (
                    <span className="hidden sm:flex items-center gap-1 text-xs text-red-500">
                      <Frown className="w-3.5 h-3.5" />{c.negativas}
                    </span>
                  )}
                  <div className="text-right">
                    <p className={`text-lg sm:text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{c.total_mensagens}</p>
                    <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>msgs</p>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RankingContatos;
